/**
 * Base Module Interface
 * 
 * All modules must extend this class and implement the required methods.
 * This provides a consistent interface for the module system.
 */
import { ReactNode } from 'react';

export interface ModuleMetadata {
    id: string;
    name: string;
    description: string;
    version: string;
    enabled: boolean;
}

export interface NavigationItem {
    name: string;
    path: string;
    icon: ReactNode;
    roles?: string[];
    children?: NavigationItem[];
    module?: string;
}

export interface RouteItem {
    path: string;
    element: ReactNode;
    roles?: string[];
    module?: string;
}

export interface DashboardWidget {
    id: string;
    title: string;
    component: string;
    module: string;
    priority?: number;
}

export interface AnalyticsData {
    [key: string]: any;
}

export interface HelpContent {
    [route: string]: {
        title: string;
        description: string;
        quickTips?: string[];
        faqs?: { question: string; answer: string; }[];
    };
}

export class BaseModule {
    id: string;
    name: string;
    description: string;
    version: string;
    enabled: boolean;

    constructor() {
        this.id = 'base-module';
        this.name = 'Base Module';
        this.description = 'Base module description';
        this.version = '1.0.0';
        this.enabled = true;
    }

    /**
     * Get navigation items for the sidebar
     * @param {Object} user - Current user object
     * @param {Object} userProfile - Current user profile
     * @returns {Array} Array of navigation items
     */
    getNavigationItems(user: any, userProfile: any): NavigationItem[] {
        return [];
    }

    /**
     * Get React routes for the module
     * @param {Object} user - Current user object
     * @param {Object} userProfile - Current user profile
     * @returns {Array} Array of route objects
     */
    getRoutes(user: any, userProfile: any): RouteItem[] {
        return [];
    }

    /**
     * Get dashboard widgets for the module
     * @param {Object} user - Current user object
     * @param {Object} userProfile - Current user profile
     * @returns {Array} Array of widget components
     */
    getDashboardWidgets(user: any, userProfile: any): DashboardWidget[] {
        return [];
    }

    /**
     * Get analytics data for the module
     * @param {Object} user - Current user object
     * @param {Object} userProfile - Current user profile
     * @returns {Object} Analytics data object
     */
    async getAnalyticsData(user: any, userProfile: any): Promise<AnalyticsData> {
        return {};
    }

    /**
     * Get personal analytics data for the module
     * @param {Object} user - Current user object
     * @param {Object} userProfile - Current user profile
     * @returns {Object} Personal analytics data object
     */
    async getPersonalAnalyticsData(user: any, userProfile: any): Promise<AnalyticsData> {
        return {};
    }

    /**
     * Get help content for the module
     * @returns {Object} Help content object
     */
    getHelpContent(user: any, userProfile: any): HelpContent {
        return {};
    }

    /**
     * Get feedback triggers for the module
     * @returns {Array} Array of feedback trigger functions
     */
    getFeedbackTriggers(): any[] {
        return [];
    }

    /**
     * Get Firestore collections used by the module
     * @returns {Array} Array of collection names
     */
    getCollections(): string[] {
        return [];
    }

    /**
     * Get Firestore security rules for the module
     * @returns {string} Security rules string
     */
    getSecurityRules(): string {
        return '';
    }

    /**
     * Initialize the module
     * Called when the module is loaded
     */
    async initialize(): Promise<void> {
        // Override in subclasses if needed
    }

    /**
     * Cleanup the module
     * Called when the module is unloaded
     */
    async cleanup(): Promise<void> {
        // Override in subclasses if needed
    }

    /**
     * Check if the module is available for the current user
     * @param {Object} user - Current user object
     * @param {Object} userProfile - Current user profile
     * @returns {boolean} Whether the module is available
     */
    isAvailable(user: any, userProfile: any): boolean {
        return this.enabled;
    }

    /**
     * Get module metadata
     * @returns {Object} Module metadata
     */
    getMetadata(): ModuleMetadata {
        return {
            id: this.id,
            name: this.name,
            description: this.description,
            version: this.version,
            enabled: this.enabled
        };
    }
}

export default BaseModule;
